import type { AnalyticsSearchStatsRecord } from '~/services/AnalyticsService';

export type GroupedSearchStats = {
  naturalLanguageQuery: string;
  count: number;
  firstSearchId: string;
  destinationTypes: Array<string>;
  destinationIds: Array<string>;
  landmarkIds: Array<string>;
  propertyIds: Array<string>;
  prefixMatchedGeometryNames: Array<string>;
  prefixMatchedOfferNames: Array<string>;
  dateTypes: Array<string>;
  durationTypes: Array<string>;
  dateRanges: Array<string>;
  flexibleMonths: Array<string>;
  occupancies: Array<string>;
};

const DEFAULT_ADULTS = 2;
const DEFAULT_CHILDREN = 0;

// 2 adults with no children is the overwhelmingly common case and not worth
// surfacing - only occupancy that differs from it is interesting to an analyst.
function isDefaultOccupancy(adults: number | null, children: number | null): boolean {
  return adults === DEFAULT_ADULTS && (children ?? DEFAULT_CHILDREN) === DEFAULT_CHILDREN;
}

function formatOccupancy(adults: number | null, children: number | null): string | null {
  if (adults == null || isDefaultOccupancy(adults, children)) {
    return null;
  }

  const parts = [`${adults} adult${adults === 1 ? '' : 's'}`];

  if (children) {
    parts.push(`${children} kid${children === 1 ? '' : 's'}`);
  }

  return parts.join(', ');
}

function uniqueFirstSeenOrder(ids: Array<string>): Array<string> {
  const seen = new Set<string>();
  const result: Array<string> = [];

  for (const id of ids) {
    if (!seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }

  return result;
}

export function groupSearchStatsByQuery(records: Array<AnalyticsSearchStatsRecord>): Array<GroupedSearchStats> {
  const buckets = new Map<string, Array<AnalyticsSearchStatsRecord>>();

  for (const record of records) {
    const bucket = buckets.get(record.naturalLanguageQuery);
    if (bucket) {
      bucket.push(record);
    } else {
      buckets.set(record.naturalLanguageQuery, [record]);
    }
  }

  const groups = Array.from(buckets.entries()).map(([naturalLanguageQuery, bucket]) => ({
    naturalLanguageQuery,
    count: bucket.length,
    firstSearchId: bucket[0].searchId,
    destinationTypes: uniqueFirstSeenOrder(
      bucket.flatMap((record) => (record.destinationType ? [record.destinationType] : [])),
    ),
    destinationIds: uniqueFirstSeenOrder(bucket.flatMap((record) => record.destinationIds)),
    landmarkIds: uniqueFirstSeenOrder(bucket.flatMap((record) => record.landmarkIds)),
    propertyIds: uniqueFirstSeenOrder(bucket.flatMap((record) => record.propertyIds)),
    prefixMatchedGeometryNames: uniqueFirstSeenOrder(bucket.flatMap((record) => record.prefixMatchedGeometryNames)),
    prefixMatchedOfferNames: uniqueFirstSeenOrder(bucket.flatMap((record) => record.prefixMatchedOfferNames)),
    dateTypes: uniqueFirstSeenOrder(
      bucket.flatMap((record) => (record.dateType && record.dateType !== 'anytime' ? [record.dateType] : [])),
    ),
    durationTypes: uniqueFirstSeenOrder(bucket.flatMap((record) => (record.durationType ? [record.durationType] : []))),
    dateRanges: uniqueFirstSeenOrder(
      bucket.flatMap((record) =>
        record.startDate && record.endDate ? [`${record.startDate} to ${record.endDate}`] : [],
      ),
    ),
    flexibleMonths: uniqueFirstSeenOrder(bucket.flatMap((record) => record.flexibleMonths)),
    occupancies: uniqueFirstSeenOrder(
      bucket.flatMap((record) => {
        const occupancy = formatOccupancy(record.adults, record.children);
        return occupancy ? [occupancy] : [];
      }),
    ),
  }));

  groups.sort((a, b) => b.count - a.count);

  return groups;
}
